import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Auth callback for Emergent-managed Google Auth.
 * Runs whenever the URL fragment contains `session_id=`.
 *
 * Emergent session_ids are ONE-TIME-USE. If a user refreshes the callback
 * URL (which still has #session_id=xxx), a second POST would fail with 401
 * because Emergent already consumed the session. To keep the UX predictable
 * we:
 *   1. Strip the fragment from the URL BEFORE calling the backend so a
 *      manual reload lands the user back on /login instead of retrying.
 *   2. Track in-flight session_ids via sessionStorage so React re-mounts
 *      cannot POST the same session twice.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    // Persistent guard so a browser refresh / react remount cannot re-post
    // the same one-time session_id.
    const guardKey = `google_auth_used_${sessionId}`;
    try {
      if (sessionStorage.getItem(guardKey)) {
        // Already consumed — go home and let AuthContext detect the cookie.
        navigate("/dashboard", { replace: true });
        return;
      }
      sessionStorage.setItem(guardKey, "1");
    } catch { /* sessionStorage unavailable — proceed anyway */ }

    // Strip the fragment now so a manual page refresh doesn't retry the
    // consumed session_id and land on an error.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch { /* ignore */ }

    (async () => {
      try {
        const { data } = await api.post("/auth/google-callback", { session_id: sessionId });
        await refresh();
        toast.success(`Welcome, ${data.name || data.email}`);
        navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Google login failed";
        toast.error(msg);
        // Clear the used-flag so user can retry cleanly with a fresh
        // "Continue with Google" click.
        try { sessionStorage.removeItem(guardKey); } catch { /* ignore */ }
        navigate("/login", { replace: true });
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#002FA7] mb-3">Signing you in</div>
        <div className="font-mono text-sm text-neutral-500">Please wait…</div>
      </div>
    </div>
  );
}
